import { useState, useMemo } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { Filter, X, ChevronRight, ArrowLeft } from 'lucide-react'
import { motion, AnimatePresence } from 'motion/react'
import { BottomSheet } from '@/shared/ui/bottom-sheet'
import { SelectionOption, SelectionList } from '@/shared/ui/selection-list'
import { cn } from '@/shared/lib/utils'
import { useTelegram } from '@/shared/lib/telegram'
import { useTeaCategories, useTeawareCategories } from '@/entities/product/api/use-categories'

export interface CatalogFiltersProps {
  currentCategory?: string
  currentSubcategory?: string
  categories?: Array<{ value: string; label: string }>
  onFiltersChange?: (filters: { category?: string; subcategory?: string }) => void
  asSheet?: boolean
  section?: 'tea' | 'teaware'
}

type ViewMode = 'categories' | 'subcategories'

/**
 * Фильтры каталога с унифицированным Bottom Sheet
 * Поддерживает drill-down в подкатегории
 */
export function CatalogFilters({
  currentCategory,
  currentSubcategory,
  categories = [],
  onFiltersChange,
  asSheet = true,
  section = 'tea',
}: CatalogFiltersProps) {
  const navigate = useNavigate()
  const { hapticFeedback } = useTelegram()
  const [isOpen, setIsOpen] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('categories')
  const [selectedCategoryForDrill, setSelectedCategoryForDrill] = useState<string | null>(null)
  
  // Получаем полные данные категорий с подкатегориями
  const { data: teaCategoriesData } = useTeaCategories()
  const { data: teawareCategoriesData } = useTeawareCategories()
  const fullCategories = section === 'tea' 
    ? teaCategoriesData?.data || []
    : teawareCategoriesData?.data || []
  
  // Находим категорию для drill-down
  const drillCategory = useMemo(() => 
    fullCategories.find(cat => cat.id === selectedCategoryForDrill),
    [fullCategories, selectedCategoryForDrill]
  )
  
  // Сортированные подкатегории
  const subcategories = useMemo(() => {
    if (!drillCategory?.subcategories) return []
    return [...drillCategory.subcategories].sort((a, b) => {
      const orderA = a.sortOrder ?? a.orderIndex ?? 999
      const orderB = b.sortOrder ?? b.orderIndex ?? 999
      return orderA - orderB
    })
  }, [drillCategory])

  // Получаем текущую subcategory из URL
  let urlSubcategory: string | undefined
  try {
    const search = useSearch({ strict: false })
    urlSubcategory = (search as any)?.subcategory
  } catch {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search)
      urlSubcategory = searchParams.get('subcategory') || undefined
    }
  }
  
  const activeSubcategory = currentSubcategory ?? urlSubcategory

  const hasActiveFilters = !!currentCategory || !!activeSubcategory

  const handleCategorySelect = (categoryId?: string) => {
    // Если выбрана категория с подкатегориями - drill down
    if (categoryId) {
      const category = fullCategories.find(c => c.id === categoryId)
      if (category?.subcategories && category.subcategories.length > 0) {
        hapticFeedback?.impactOccurred('light')
        setSelectedCategoryForDrill(categoryId)
        setViewMode('subcategories')
        return
      }
    }
    
    // Иначе применяем фильтр сразу
    applyFilters(categoryId, undefined)
  }
  
  const handleSubcategorySelect = (subcategoryId?: string) => {
    applyFilters(selectedCategoryForDrill || undefined, subcategoryId)
  }
  
  const applyFilters = (category?: string, subcategory?: string) => {
    if (onFiltersChange) {
      onFiltersChange({ category, subcategory })
    } else {
      navigate({
        search: (prev: Record<string, unknown>) => ({ 
          ...prev, 
          category: category || undefined,
          subcategory: subcategory || undefined,
        }),
      } as any)
    }
    setIsOpen(false)
    // Сбрасываем состояние при закрытии
    setTimeout(() => {
      setViewMode('categories')
      setSelectedCategoryForDrill(null)
    }, 300)
  }

  const handleClearFilters = () => {
    hapticFeedback?.impactOccurred('light')
    applyFilters(undefined, undefined)
  }
  
  const handleBack = () => {
    hapticFeedback?.impactOccurred('light')
    setViewMode('categories')
    setSelectedCategoryForDrill(null)
  }
  
  const handleClose = () => {
    setIsOpen(false)
    // Сбрасываем состояние при закрытии
    setTimeout(() => {
      setViewMode('categories')
      setSelectedCategoryForDrill(null)
    }, 300)
  }
  
  // Проверяем, есть ли у категории подкатегории
  const categoryHasSubcategories = (categoryId: string) => {
    const cat = fullCategories.find(c => c.id === categoryId)
    return cat?.subcategories && cat.subcategories.length > 0
  }
  
  // Получаем название категории по ID
  const getCategoryName = (categoryId: string) => {
    const cat = categories.find(c => c.value === categoryId)
    return cat?.label || ''
  }

  if (asSheet) {
    return (
      <>
        <button
          onClick={() => {
            hapticFeedback?.impactOccurred('light')
            setIsOpen(true)
          }}
          className={cn(
            'flex-shrink-0 px-4 rounded-[14px]',
            'transition-all font-medium text-sm',
            'flex items-center gap-2 shadow-sm dark:shadow-md',
            'min-h-[44px]',
            'active:scale-[0.98] transition-transform duration-100',
            {
              'bg-primary/20 dark:bg-primary-dark/20 border-2 border-primary dark:border-primary-dark text-primary dark:text-primary-light': hasActiveFilters,
              'bg-white/80 backdrop-blur-sm border border-white/18 text-ink hover:bg-white/90 hover:border-primary/30 dark:bg-dark-elevated dark:border-dark-border dark:text-white dark:hover:bg-dark-card': !hasActiveFilters,
            }
          )}
        >
          <Filter className="h-4 w-4" strokeWidth={1.5} />
          <span>Фильтры</span>
        </button>

        <BottomSheet isOpen={isOpen} onClose={handleClose} height={60}>
          <div className="px-4 pb-6 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <AnimatePresence mode="wait">
                  {viewMode === 'subcategories' && (
                    <motion.button
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.15 }}
                      onClick={handleBack}
                      className="p-1.5 -ml-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-elevated transition-colors"
                    >
                      <ArrowLeft className="h-5 w-5 text-ink dark:text-white" />
                    </motion.button>
                  )}
                </AnimatePresence>
                <motion.h3 
                  key={viewMode === 'categories' ? 'filters' : drillCategory?.name}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="text-lg font-semibold text-ink dark:text-white"
                >
                  {viewMode === 'categories' ? 'Фильтры' : drillCategory?.name}
                </motion.h3>
              </div>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="text-sm text-primary dark:text-primary-light flex items-center gap-1"
                >
                  <X className="h-4 w-4" />
                  Сбросить
                </button>
              )}
            </div>

            {/* Content with slide animation */}
            <div className="relative overflow-hidden">
              <AnimatePresence mode="wait" initial={false}>
                {viewMode === 'categories' ? (
                  <motion.div
                    key="categories"
                    initial={{ opacity: 0, x: -100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                  >
                    {categories.length > 0 && (
                      <SelectionList title="Направление">
                        <SelectionOption
                          selected={!currentCategory && !activeSubcategory}
                          onClick={() => applyFilters(undefined, undefined)}
                          label="Все направления"
                        />
                        {categories.map((category) => {
                          const hasSubcats = categoryHasSubcategories(category.value)
                          const isSelected = currentCategory === category.value && !activeSubcategory
                          
                          return (
                            <SelectionOption
                              key={category.value}
                              selected={isSelected}
                              onClick={() => handleCategorySelect(category.value)}
                              label={category.label}
                              rightContent={hasSubcats ? (
                                <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                              ) : undefined}
                            />
                          )
                        })}
                      </SelectionList>
                    )}
                  </motion.div>
                ) : (
                  <motion.div
                    key="subcategories"
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 100 }}
                    transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                  >
                    <SelectionList title="Подкатегория">
                      <SelectionOption
                        selected={currentCategory === selectedCategoryForDrill && !activeSubcategory}
                        onClick={() => applyFilters(selectedCategoryForDrill || undefined, undefined)}
                        label={`Все ${drillCategory?.name?.toLowerCase() || ''}`}
                      />
                      {subcategories.map((subcategory) => (
                        <SelectionOption
                          key={subcategory.id}
                          selected={activeSubcategory === subcategory.id}
                          onClick={() => handleSubcategorySelect(subcategory.id)}
                          label={subcategory.name}
                        />
                      ))}
                    </SelectionList>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </BottomSheet>
      </>
    )
  }

  return (
    <select
      value={currentCategory || ''}
      onChange={(e) => applyFilters(e.target.value || undefined, undefined)}
      className="px-4 py-2 rounded-lg border border-gray-300 bg-white"
    >
      <option value="">Все категории</option>
      {categories.map((category) => (
        <option key={category.value} value={category.value}>
          {category.label}
        </option>
      ))}
    </select>
  )
}
